"use client";

import { useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Badge } from "@/app/components/ui/badge";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Slider } from "@/app/components/ui/slider";
import { Checkbox } from "@/app/components/ui/checkbox";
import { CopyButton } from "@/app/components/ui/copy-button";
import { 
  User, 
  Copy, 
  Download, 
  RotateCcw, 
  Settings, 
  Info, 
  AlertTriangle, 
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  Building,
  Globe,
  Hash,
  BarChart3,
  History,
  Save,
  Trash2,
  RefreshCw,
  Database,
  Lock,
  Clock,
  Fingerprint,
  Layers,
  Target,
  Sparkles,
  Users,
  UserCheck,
  FileText,
  Shield,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

interface GeneratedIdentity {
  id: string;
  data: IdentityData;
  timestamp: Date;
  type: string;
  settings: any;
}

interface IdentityData {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    fullAddress: string;
  };
  personal: {
    age: number;
    birthDate: string;
    gender: string;
    ssn: string;
  };
  professional: {
    jobTitle: string;
    company: string;
    industry: string;
    salary: number;
  };
  financial: {
    creditCard: string;
    bankAccount: string;
    routingNumber: string;
  };
  online: {
    username: string;
    website: string;
    socialMedia: {
      twitter: string;
      linkedin: string;
      instagram: string;
    };
  };
}

interface IdentityHistory {
  id: string;
  identities: IdentityData[];
  timestamp: Date;
  count: number;
  settings: any;
}

type GeneratorType = "basic" | "detailed" | "professional" | "financial";

// Sample data arrays removed - now handled server-side via TRPC
// Only keeping countries array for UI dropdown
const countries = ["United States", "Canada", "United Kingdom", "Australia", "Germany", "France", "Japan"];

export default function FakeIdentityGeneratorPage() {
  const [generatedIdentities, setGeneratedIdentities] = useState<GeneratedIdentity[]>([]);
  const [history, setHistory] = useState<IdentityHistory[]>([]);
  const [activeTab, setActiveTab] = useState<GeneratorType>("basic");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [showSensitiveData, setShowSensitiveData] = useState(false);

  // Settings
  const [count, setCount] = useState(1);
  const [includeFinancial, setIncludeFinancial] = useState(false);
  const [includeProfessional, setIncludeProfessional] = useState(false);
  const [includeOnline, setIncludeOnline] = useState(false);
  const [country, setCountry] = useState("United States");

  // TRPC mutations
  const generateBasicMutation = trpc.fakeIdentity.generateBasic.useMutation();
  const generateProfessionalMutation = trpc.fakeIdentity.generateProfessional.useMutation();
  const generateFinancialMutation = trpc.fakeIdentity.generateFinancial.useMutation();
  const generateDetailedMutation = trpc.fakeIdentity.generate.useMutation();

  const generateIdentities = useCallback(async () => {
    try {
      let result: any;
      let type = "";
      let settings: any = {};

      switch (activeTab) {
        case "basic":
          result = await generateBasicMutation.mutateAsync({
            count,
            country
          });
          type = "Basic Identity";
          settings = { count, country };
          break;
        case "detailed":
          result = await generateDetailedMutation.mutateAsync({
            count,
            includeFinancial,
            includeProfessional,
            includeOnline,
            country
          });
          type = "Detailed Identity";
          settings = { count, includeFinancial, includeProfessional, includeOnline, country };
          break;
        case "professional":
          result = await generateProfessionalMutation.mutateAsync({
            count,
            includeOnline,
            country
          });
          type = "Professional Identity";
          settings = { count, includeProfessional: true, includeOnline, country };
          break;
        case "financial":
          result = await generateFinancialMutation.mutateAsync({
            count,
            includeProfessional,
            country
          });
          type = "Financial Identity";
          settings = { count, includeFinancial: true, includeProfessional, country };
          break;
      }

      if (result.success) {
        const identities = result.identities;

        // Add to generated identities
        const newGeneratedIdentities = identities.map((data: IdentityData, index: number) => ({
          id: `${Date.now()}-${index}`,
          data,
          timestamp: new Date(),
          type,
          settings
        }));

        setGeneratedIdentities(prev => [...newGeneratedIdentities, ...prev]);

        // Add to history
        const historyEntry: IdentityHistory = {
          id: Date.now().toString(),
          identities,
          timestamp: new Date(),
          count: identities.length,
          settings
        };

        setHistory(prev => [historyEntry, ...prev.slice(0, 49)]); // Keep last 50 entries

        toast.success(`Generated ${identities.length} fake identit${identities.length > 1 ? 'ies' : 'y'}!`);
      }
    } catch (error) {
      toast.error(`Failed to generate identities: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [activeTab, count, includeFinancial, includeProfessional, includeOnline, country, generateBasicMutation, generateProfessionalMutation, generateFinancialMutation, generateDetailedMutation]);

  const copyToClipboard = useCallback(async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (index !== undefined) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setGeneratedIdentities([]);
    toast.success("History cleared!");
  }, []);

  const exportIdentities = useCallback(() => {
    const data = {
      generated: generatedIdentities,
      history: history,
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fake-identities-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Identities exported!");
  }, [generatedIdentities, history]);

  const getTotalCount = useMemo(() => {
    return generatedIdentities.length;
  }, [generatedIdentities]);

  const getUniqueCountries = useMemo(() => {
    const countries = new Set(generatedIdentities.map(item => item.data.address.country));
    return countries.size;
  }, [generatedIdentities]);

  const getAverageAge = useMemo(() => {
    if (generatedIdentities.length === 0) return 0;
    const totalAge = generatedIdentities.reduce((sum, item) => sum + item.data.personal.age, 0);
    return totalAge / generatedIdentities.length;
  }, [generatedIdentities]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Fake Identity Generator</h1>
        <p className="text-muted-foreground">
          Generate realistic fake identities for testing, development, and creating sample data. Perfect for prototyping and demos.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator Controls */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Identity Generator
              </CardTitle>
              <CardDescription>
                Choose your generator type and configure the settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as GeneratorType)}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic">Basic</TabsTrigger>
                  <TabsTrigger value="detailed">Detailed</TabsTrigger>
                  <TabsTrigger value="professional">Professional</TabsTrigger>
                  <TabsTrigger value="financial">Financial</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={50}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((countryName) => (
                          <SelectItem key={countryName} value={countryName}>
                            {countryName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Basic identity includes name, email, phone, address, age, and SSN.
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="detailed" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={50}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((countryName) => (
                          <SelectItem key={countryName} value={countryName}>
                            {countryName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="includeProfessional"
                        checked={includeProfessional}
                        onCheckedChange={(checked) => setIncludeProfessional(checked as boolean)}
                      />
                      <Label htmlFor="includeProfessional">Include Professional Information</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="includeFinancial"
                        checked={includeFinancial}
                        onCheckedChange={(checked) => setIncludeFinancial(checked as boolean)}
                      />
                      <Label htmlFor="includeFinancial">Include Financial Information</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="includeOnline"
                        checked={includeOnline}
                        onCheckedChange={(checked) => setIncludeOnline(checked as boolean)}
                      />
                      <Label htmlFor="includeOnline">Include Online Presence</Label>
                    </div>
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Detailed identity allows you to customize which additional information to include.
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="professional" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={50}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((countryName) => (
                          <SelectItem key={countryName} value={countryName}>
                            {countryName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeOnline"
                      checked={includeOnline}
                      onCheckedChange={(checked) => setIncludeOnline(checked as boolean)}
                    />
                    <Label htmlFor="includeOnline">Include Online Presence</Label>
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Professional identity includes job title, company, industry, and salary information.
                    </AlertDescription>
                  </Alert>
                </TabsContent>

                <TabsContent value="financial" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={50}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((countryName) => (
                          <SelectItem key={countryName} value={countryName}>
                            {countryName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeProfessional"
                      checked={includeProfessional}
                      onCheckedChange={(checked) => setIncludeProfessional(checked as boolean)}
                    />
                    <Label htmlFor="includeProfessional">Include Professional Information</Label>
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Financial identity includes credit card, bank account, and routing number information.
                    </AlertDescription>
                  </Alert>
                </TabsContent>
              </Tabs>

              <div className="flex gap-2">
                <Button 
                  onClick={generateIdentities} 
                  className="flex-1"
                  disabled={
                    generateBasicMutation.isPending ||
                    generateProfessionalMutation.isPending ||
                    generateFinancialMutation.isPending ||
                    generateDetailedMutation.isPending
                  }
                >
                  {(generateBasicMutation.isPending ||
                    generateProfessionalMutation.isPending ||
                    generateFinancialMutation.isPending ||
                    generateDetailedMutation.isPending) ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <User className="h-4 w-4 mr-2" />
                      Generate Identities
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={clearHistory}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear
                </Button>
                <Button variant="outline" onClick={exportIdentities}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Generated Identities Display */}
          {generatedIdentities.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Generated Identities
                </CardTitle>
                <CardDescription className="flex items-center justify-between">
                  <span>{generatedIdentities.length} identit{generatedIdentities.length > 1 ? 'ies' : 'y'} generated</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSensitiveData(!showSensitiveData)}
                  >
                    {showSensitiveData ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    {showSensitiveData ? 'Hide' : 'Show'} Sensitive Data
                  </Button>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {generatedIdentities.slice(0, 10).map((item, index) => (
                    <div
                      key={item.id}
                      className="p-4 border rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="secondary">{item.type}</Badge>
                        <CopyButton
                          text={JSON.stringify(item.data, null, 2)}
                          onCopy={() => copyToClipboard(JSON.stringify(item.data, null, 2), index)}
                          className="h-8 w-8"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{item.data.fullName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{item.data.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{item.data.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{item.data.address.fullAddress}</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">Age: {item.data.personal.age} ({item.data.personal.gender})</span>
                          </div>
                          {showSensitiveData && (
                            <div className="flex items-center gap-2">
                              <Hash className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">SSN: {item.data.personal.ssn}</span>
                            </div>
                          )}
                          {item.data.professional.jobTitle && (
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{item.data.professional.jobTitle} at {item.data.professional.company}</span>
                            </div>
                          )}
                          {showSensitiveData && item.data.financial.creditCard && (
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">Card: {item.data.financial.creditCard}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {generatedIdentities.length > 10 && (
                  <p className="text-sm text-muted-foreground mt-4">
                    Showing first 10 identities. Total: {generatedIdentities.length}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Statistics and History */}
        <div className="space-y-6">
          {/* Statistics */}
          {generatedIdentities.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getTotalCount}</div>
                    <div className="text-sm text-muted-foreground">Total Identities</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getUniqueCountries}</div>
                    <div className="text-sm text-muted-foreground">Countries</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getAverageAge.toFixed(1)}</div>
                    <div className="text-sm text-muted-foreground">Avg Age</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">
                      {generatedIdentities.filter(item => item.data.professional.jobTitle).length}
                    </div>
                    <div className="text-sm text-muted-foreground">With Jobs</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Recent History
                </CardTitle>
                <CardDescription>
                  Last {history.length} generation{history.length > 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.slice(0, 10).map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {entry.count} identit{entry.count > 1 ? 'ies' : 'y'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp || Date.now()).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.identities.slice(0, 2).map((identity, index) => (
                          <div key={index} className="truncate">
                            {identity.fullName} - {identity.email}
                          </div>
                        ))}
                        {entry.identities.length > 2 && (
                          <span>+{entry.identities.length - 2} more</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Usage Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Usage Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Perfect for testing applications and creating sample data</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Use for prototyping user interfaces and demos</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Toggle sensitive data visibility for security</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Export data as JSON for use in other applications</span>
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>Disclaimer:</strong> This tool generates fake data for testing and development purposes only. 
              Do not use this data for any illegal activities or to impersonate real people.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
}
